import {
    Column,
    Container,
    Image,
    Panel9Slice,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {Localization} from '../../utils/Localization.js';
import {ShoppeBase} from './shoppe-base.js';
import {MenuPanels} from '../../models/UIModel.js';
import {goToGame} from '../utils/goToGame.js';
import {
    Align,
    FlexDirection,
    Justify,
    PositionType,
    TextProps,
} from '@wonderlandengine/react-ui';
import {Texture} from '@wonderlandengine/api';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {use, useEffect, useMemo, useState} from 'react';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {formatString, parseColor} from '../utils/utils.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {Statistics} from '../../components/statistics.js';
import {loop, stop} from '../utils/animation-context.js';
import {Easing, lerpArr} from '../../utils/lerp.js';
import {useAnimation} from '../hooks/useAnimation.js';
import {goToPowerupStorePopup} from '../utils/goToPowerupStorePopup.js';
import {StoreInventoryItems} from '../../enums/StoreInventoryItems.js';
import {goToMenu} from '../utils/goToMenu.js';
import {
    powerupMaterials,
    PowerupMaterialType,
    usePowerupMaterial,
} from '../hooks/usePowerupMaterial.js';

const PowerupButton = (
    props: React.PropsWithChildren<TextProps> & {
        icon: Texture;
        title: string;
        storeItem: StoreInventoryItems;
        upgradeLevelStat: TransactionalNames;
        inventoryStat: TransactionalNames;
        isComingSoon?: boolean;
    }
) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const shadowMaterial = rotbMats?.buttonShadowMaterial;
    const baseMaterial = rotbMats?.buttonMaterial as FlatOpaqueTextured;

    const defaultButtonMaterial = usePowerupMaterial(
        PowerupMaterialType.Default,
        baseMaterial,
        colorSwatch.PowerupPerksButton
    );

    const runOutMaterial = usePowerupMaterial(
        PowerupMaterialType.RunOut,
        baseMaterial,
        colorSwatch.PowerupPerksButtonRefill1
    );
    const comingSoonMaterial = usePowerupMaterial(
        PowerupMaterialType.ComingSoon,
        baseMaterial,
        colorSwatch.GenericButton
    );

    const lockedMaterial = usePowerupMaterial(
        PowerupMaterialType.Locked,
        baseMaterial,
        colorSwatch.PowerupPerksButtonLocked
    );

    const divotEmpty = usePowerupMaterial(
        PowerupMaterialType.DivotEmpty,
        baseMaterial,
        colorSwatch.UpgradeDivotEmpty
    );

    const divotFull = usePowerupMaterial(
        PowerupMaterialType.DivotFull,
        baseMaterial,
        colorSwatch.UpgradeDivotFull
    );

    const currentUpgradeLevel = Statistics.instance.getTransactional(
        props.upgradeLevelStat
    );

    const isLocked = Statistics.instance.getTransactional(props.upgradeLevelStat) === 0;

    const divots = [];

    for (let i = 0; i < 5; i++) {
        if (i < currentUpgradeLevel) {
            divots.push(true);
        } else {
            divots.push(false);
        }
    }

    const [currentInventoryLevel, setCurrentInventoryLevel] = useState(
        Statistics.instance.getTransactional(props.inventoryStat)
    );

    const [buttonMaterial, setButtonMaterial] = useState(() => {
        if (props.isComingSoon) {
            return comingSoonMaterial;
        }
        if (isLocked) {
            return lockedMaterial;
        }
        if (currentInventoryLevel === 0) {
            return runOutMaterial;
        }
        return defaultButtonMaterial;
    });

    useEffect(() => {
        if (currentInventoryLevel > 0 || props.isComingSoon) {
            return;
        }
        const anim = loop((f) => {
            if (!powerupMaterials.has(PowerupMaterialType.RunOut)) {
                return;
            }
            const s = (Math.sin(f * Math.PI * 2) + 1) / 2;
            let c = [];

            lerpArr(
                c,
                Array.from(parseColor(colorSwatch.PowerupPerksButtonRefill1)),
                Array.from(parseColor(colorSwatch.PowerupPerksButtonRefill2)),
                s
            );
            powerupMaterials.get(PowerupMaterialType.RunOut)!.setColor(c);
        }, 1);
        return () => stop(anim);
    }, [currentInventoryLevel]);

    const [popAnimationRef, popSetReference] = useAnimation({
        position: {from: [0, 0], to: [0, 10]},
        duration: 250,
        repeat: true,
        yoyo: true,
        easing: Easing.InOutCubic,
    });

    useEffect(() => {
        if (currentInventoryLevel === 0 && !props.isComingSoon && !isLocked) {
            popSetReference(popAnimationRef.current);
        } else {
            popSetReference(null);
        }
    }, [currentInventoryLevel, isLocked, props.isComingSoon]);

    const onClickPerk = () => {
        if (props.isComingSoon) {
            return;
        }
        goToPowerupStorePopup(props.storeItem, false, props.icon);
    };

    return (
        <Container>
            <Container
                left={4}
                top={6}
                onClick={() => {
                    onClickPerk();
                }}
            >
                <Panel9Slice
                    // ref={ref}
                    borderSize={8}
                    borderTextureSize={0.0625}
                    material={shadowMaterial}
                >
                    <Panel9Slice
                        height={108}
                        width={500}
                        paddingLeft={props.text || props.children ? 12 : 0}
                        left={-4}
                        top={-6}
                        material={buttonMaterial}
                        borderSize={8}
                        borderTextureSize={0.0625}
                        flexDirection={FlexDirection.Row}
                        alignItems={Align.Center}
                        alignContent={Align.Stretch}
                        justifyContent={Justify.SpaceBetween}
                    >
                        <Row
                            margin={20}
                            width={460}
                            alignItems={Align.Center}
                            justifyContent={Justify.Center}
                        >
                            <Column gap={2} alignItems={Align.Center}>
                                <Image
                                    height={70}
                                    width={70}
                                    rounding={0}
                                    src={
                                        isLocked && !props.isComingSoon
                                            ? UiTexturesManager.instance.buttonLock
                                            : props.icon
                                    }
                                ></Image>
                                <Row gap={3}>
                                    {!isLocked &&
                                        divots.map((divot, index) => {
                                            return (
                                                <Panel9Slice
                                                    key={index}
                                                    height={12}
                                                    width={12}
                                                    rounding={0}
                                                    border={2}
                                                    borderTextureSize={0.0625}
                                                    material={
                                                        divot ? divotFull : divotEmpty
                                                    }
                                                ></Panel9Slice>
                                            );
                                        })}
                                </Row>
                                {!props.isComingSoon && !isLocked && (
                                    <Text
                                        z={0.01}
                                        fontSize={28}
                                        position={PositionType.Absolute}
                                        width={90}
                                        top={40}
                                        material={UiTexturesManager.instance.uiTextOutline}
                                        textAlign="right"
                                        ref={popAnimationRef}
                                    >
                                        {currentInventoryLevel}
                                    </Text>
                                )}
                            </Column>

                            <Column
                                flexGrow={1}
                                alignItems={Align.Center}
                                justifyContent={Justify.Center}
                                gap={20}
                            >
                                <Text
                                    //  top={4}
                                    textAlign={'center'}
                                    flexGrow={1}
                                    // top={-(props.fontSize ?? 32) / 2}
                                    fontSize={28}
                                    text={props.title}
                                ></Text>

                                <Text
                                    //  top={4}
                                    textAlign={'center'}
                                    flexGrow={1}
                                    // top={-(props.fontSize ?? 32) / 2}
                                    fontSize={20}
                                    text={
                                        props.isComingSoon
                                            ? Localization.instance.get('ComingSoon')
                                            : isLocked
                                              ? Localization.instance.get('UnlockNow')
                                              : formatString(
                                                    Localization.instance.get(
                                                        'UpgradeLevel'
                                                    ),
                                                    currentUpgradeLevel
                                                )
                                    }
                                ></Text>
                            </Column>
                        </Row>
                        {props.isComingSoon && (
                            <Image
                                alignSelf={Align.FlexEnd}
                                position={PositionType.Absolute}
                                right={0}
                                width={100}
                                height={100}
                                rounding={5}
                                margin={10}
                                src={UiTexturesManager.instance.comingSoonBanner}
                            ></Image>
                        )}
                    </Panel9Slice>
                </Panel9Slice>
            </Container>
        </Container>
    );
};

const PerksButton = () => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const shadowMaterial = rotbMats?.buttonShadowMaterial;
    const baseMaterial = rotbMats?.buttonMaterial as FlatOpaqueTextured;
    const bm = usePowerupMaterial(
        PowerupMaterialType.Perk,
        baseMaterial,
        colorSwatch.PowerupPerksButtonDark
    );

    return (
        <Container>
            <Container
                left={4}
                top={6}
                onClick={() => {
                    goToMenu(MenuPanels.Loadout2);
                }}
            >
                <Panel9Slice
                    // ref={ref}
                    borderSize={8}
                    borderTextureSize={0.0625}
                    material={shadowMaterial}
                >
                    <Panel9Slice
                        height={70}
                        width={500}
                        left={-4}
                        top={-6}
                        material={bm}
                        borderSize={8}
                        borderTextureSize={0.0625}
                        flexDirection={FlexDirection.Row}
                        alignItems={Align.Center}
                        alignContent={Align.Stretch}
                        justifyContent={Justify.SpaceBetween}
                        paddingLeft={40}
                        paddingRight={40}
                    >
                        <Image
                            width={40}
                            height={40}
                            rounding={0}
                            src={UiTexturesManager.instance.iconPerk}
                        ></Image>
                        <Text
                            textAlign="center"
                            flexGrow={1}
                            fontSize={28}
                            text={Localization.instance.get('Perks')}
                        ></Text>
                        <Image
                            width={40}
                            height={40}
                            rounding={0}
                            src={UiTexturesManager.instance.iconCherry}
                        ></Image>
                    </Panel9Slice>
                </Panel9Slice>
            </Container>
        </Container>
    );
};

export const LoadoutPowerups = () => {
    return (
        <ShoppeBase
            title={Localization.instance.get('Powerups')}
            onOkay={({e}) => goToGame(e)}
            showBackButton={true}
            currentMenu={MenuPanels.Loadout}
            buttonText={Localization.instance.get('Play')}
            animateButton={true}
        >
            <Column
                marginTop={20}
                height={560}
                justifyContent={Justify.SpaceBetween}
                alignItems={Align.Center}
            >
                <PowerupButton
                    storeItem={StoreInventoryItems.coconut}
                    title={Localization.instance.get('CoconutBombs')}
                    icon={UiTexturesManager.instance.buttonBuyBomb}
                    upgradeLevelStat={TransactionalNames.BombUpgrade}
                    inventoryStat={TransactionalNames.BombCount}
                ></PowerupButton>
                <PowerupButton
                    storeItem={StoreInventoryItems.smasher}
                    title={Localization.instance.get('FruitSmashers')}
                    icon={UiTexturesManager.instance.buttonFruitsmasher}
                    upgradeLevelStat={TransactionalNames.FruitSmasherUpgrade}
                    inventoryStat={TransactionalNames.FruitSmasherCount}
                ></PowerupButton>
                <PowerupButton
                    isComingSoon={true}
                    storeItem={StoreInventoryItems.barrier}
                    title={Localization.instance.get('Barriers')}
                    icon={UiTexturesManager.instance.buttonShield}
                    upgradeLevelStat={TransactionalNames.ForcefieldUpgrade}
                    inventoryStat={TransactionalNames.ForcefieldCount}
                ></PowerupButton>
                <PowerupButton
                    isComingSoon={true}
                    storeItem={StoreInventoryItems.nuke}
                    title={Localization.instance.get('Nukes')}
                    icon={UiTexturesManager.instance.theNuke}
                    upgradeLevelStat={TransactionalNames.NukeUpgrade}
                    inventoryStat={TransactionalNames.NukeCount}
                ></PowerupButton>
                <Container height={10}></Container>
                <PerksButton></PerksButton>
            </Column>
        </ShoppeBase>
    );
};
